/* Client-side variant resolver with three defence layers:

   1. CAPTURE-PHASE INTERCEPT — runs before Shopify's variant-selects
      processes a change. Records "user intent" (last option the user
      clicked) and pre-corrects radios so Shopify sees a valid combo.

   2. variant:change LISTENER — runs after Shopify has resolved. If
      Shopify's resolution didn't preserve the user's intent (because
      their combination doesn't exist), re-correct radios and
      re-dispatch a change so Shopify lands on a valid variant.

   3. MUTATION OBSERVER — runs whenever Shopify re-renders the section
      HTML (which can revert any client-side correction). Re-checks
      state and re-applies the correction if needed.

   Why three layers? Different themes/versions of Shopify's
   variant-selects behave differently:
     • Some fall back to "first available" overall, dropping the
       user's just-clicked option.
     • Some set variant_id=null and don't re-render at all, leaving
       the previous selection visually stuck.
     • Some DO re-render but their server-side render picks a variant
       that doesn't honour user intent.
   Capture-phase handles cases 1+2 cleanly. The variant:change and
   MutationObserver layers catch case 3.

   Enable debug logging in console:  localStorage.setItem('mgb_debug','1')
*/
(function () {
  var SYNC_FLAG = false;
  var lastUserIntent = null; // { optionName, value, optionIdx, pi }

  function log() {
    try {
      if (localStorage.getItem('mgb_debug') !== '1') return;
      var args = ['[mgb-variant-resolver]'].concat(Array.prototype.slice.call(arguments));
      console.log.apply(console, args);
    } catch (e) {}
  }

  function readVariants(pi) {
    var el = pi.querySelector('[data-mgb-product-variants]');
    if (!el) return null;
    try { return JSON.parse(el.textContent || '[]'); }
    catch (e) { return null; }
  }

  /* Parse an option name out of an input's `name` attribute.
     Supports multiple naming conventions seen in the wild:
       • Hidden / dropdown inputs:   name="options[Case Colour]"
       • Some themes:                name="Case Colour-{section.id}"
       • Dawn-style with position:   name="Case Colour-1"
                                      (option.position trailing number,
                                       often with a literal "\n" in
                                       the attribute due to Liquid
                                       template line breaks)
     Falls back to reading the option name from the enclosing
     fieldset's <legend> (which is where the variant-picker snippet
     outputs `{{ option.name }}:`) if the name attribute is
     unparseable. */
  // Aggressive whitespace pattern: standard \s plus NBSP, ZWS family,
  // BOM, and other invisible separators that Shopify/Liquid sometimes
  // injects into attribute values.
  var WS_RE = /[\s   -‍  　﻿]+/g;

  function parseOptionName(input, sectionId) {
    if (!input || !input.name) return null;
    var raw = String(input.name);
    var name = raw.replace(WS_RE, '');

    // 1. options[X]
    var m = name.match(/^options\[(.+)\]$/);
    if (m) return m[1];

    // 2. X-{sectionId}
    if (sectionId) {
      var sid = String(sectionId).replace(WS_RE, '');
      if (sid && name.length > sid.length + 1) {
        var suffix = '-' + sid;
        if (name.slice(-suffix.length) === suffix) {
          return name.slice(0, -suffix.length);
        }
      }
    }

    // 3. X-N (trailing integer, e.g. option.position)
    m = name.match(/^(.+)-(\d+)$/);
    if (m) return m[1];

    // 3b. Permissive variant: any "X-Y" split on the LAST dash
    var lastDash = name.lastIndexOf('-');
    if (lastDash > 0 && lastDash < name.length - 1) {
      var tail = name.slice(lastDash + 1);
      // Tail looks "id-ish" — accept if numeric or pure alphanumeric
      if (/^[A-Za-z0-9_-]+$/.test(tail)) {
        return name.slice(0, lastDash);
      }
    }

    // 4. Parse from enclosing fieldset's legend
    var fieldset = input.closest && input.closest('fieldset');
    if (fieldset) {
      // Prefer the specific MGB label-left span which holds
      // "{{ option.name }}:" as its first text node.
      var labelLeft = fieldset.querySelector('.variant-picker__label-left');
      if (labelLeft && labelLeft.firstChild && labelLeft.firstChild.nodeType === 3) {
        var text = (labelLeft.firstChild.textContent || '').trim();
        if (text.charAt(text.length - 1) === ':') text = text.slice(0, -1);
        text = text.trim();
        if (text) return text;
      }
      // Fallback: legend.textContent up to the colon
      var legend = fieldset.querySelector('legend');
      if (legend) {
        var lt = (legend.textContent || '').trim();
        var colonIdx = lt.indexOf(':');
        if (colonIdx > 0) lt = lt.slice(0, colonIdx).trim();
        if (lt) return lt;
      }
    }

    return null;
  }

  function getSectionId(pi) {
    var vs = pi.querySelector('variant-selects');
    return (vs && vs.getAttribute('data-section')) || pi.getAttribute('data-section') || '';
  }

  function getOptionNamesInOrder(pi) {
    var sectionId = getSectionId(pi);
    var names = [];
    // Single query in DOM order, mixing radios + selects, so options
    // render in the same order Shopify defined them.
    pi.querySelectorAll('variant-selects input[type="radio"], variant-selects select').forEach(function (el) {
      var n = parseOptionName(el, sectionId);
      if (n && names.indexOf(n) === -1) names.push(n);
    });
    return names;
  }

  function radiosForOption(pi, optName, sectionId) {
    sectionId = sectionId || getSectionId(pi);
    var all = pi.querySelectorAll('variant-selects input[type="radio"]');
    var out = [];
    all.forEach(function (r) {
      if (parseOptionName(r, sectionId) === optName) out.push(r);
    });
    return out;
  }

  function selectForOption(pi, optName, sectionId) {
    sectionId = sectionId || getSectionId(pi);
    var all = pi.querySelectorAll('variant-selects select');
    for (var i = 0; i < all.length; i++) {
      if (parseOptionName(all[i], sectionId) === optName) return all[i];
    }
    return null;
  }

  function getCurrentSelections(pi, optionNames) {
    var sectionId = getSectionId(pi);
    return optionNames.map(function (optName) {
      var sel = selectForOption(pi, optName, sectionId);
      if (sel) return sel.value;
      var group = radiosForOption(pi, optName, sectionId);
      for (var i = 0; i < group.length; i++) if (group[i].checked) return group[i].value;
      return null;
    });
  }

  function findBestVariant(variants, selections, pinnedIdx) {
    var i, v, j, match, exactAvail, exactAny, pinnedAvail, pinnedAny, firstAvail, firstAny;
    // Single pass — collect candidates and pick best fallback.
    // Stock state (variant.available) is preferred but NOT required,
    // because products can be entirely sold out yet still need a
    // valid selection in the UI.
    for (i = 0; i < variants.length; i++) {
      v = variants[i];
      if (!v.options) continue;

      // Exact match against current selections
      match = true;
      for (j = 0; j < selections.length; j++) {
        if (selections[j] != null && v.options[j] !== selections[j]) { match = false; break; }
      }
      if (match) {
        if (v.available && !exactAvail) exactAvail = v;
        if (!exactAny) exactAny = v;
      }

      // Pinned-option match (preserves the option the user just clicked)
      if (pinnedIdx != null && pinnedIdx >= 0 && v.options[pinnedIdx] === selections[pinnedIdx]) {
        if (v.available && !pinnedAvail) pinnedAvail = v;
        if (!pinnedAny) pinnedAny = v;
      }

      // Any variant
      if (v.available && !firstAvail) firstAvail = v;
      if (!firstAny) firstAny = v;
    }
    // Priority order: exact-available, exact-any, pinned-available,
    // pinned-any, first-available, first-any.
    return exactAvail || exactAny || pinnedAvail || pinnedAny || firstAvail || firstAny || null;
  }

  function findLabelFor(pi, radio) {
    // Prefer an enclosing <label> wrapping the radio
    var l = radio.closest && radio.closest('label');
    if (l && l !== radio) return l;
    // label[for] match — walk manually because radio.id may contain
    // unescaped characters (newlines etc.) that break CSS selectors.
    if (radio.id) {
      var labels = pi.querySelectorAll('label[for]');
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].getAttribute('for') === radio.id) return labels[i];
      }
    }
    // Next-sibling fallback (Dawn swatch pattern: <input><label>)
    var sib = radio.nextElementSibling;
    if (sib && sib.tagName === 'LABEL') return sib;
    // Skip over text-only nodes
    var n = radio.nextSibling;
    while (n) {
      if (n.nodeType === 1) { // element
        if (n.tagName === 'LABEL' || (n.classList && n.classList.contains('swatch-input__link'))) return n;
        break;
      }
      n = n.nextSibling;
    }
    return null;
  }

  function applyVariant(pi, variant, optionNames) {
    var sectionId = getSectionId(pi);
    optionNames.forEach(function (optName, idx) {
      var expected = variant.options[idx];
      if (expected == null) return;
      // Radios (works for both name="options[X]" and name="X-section.id")
      var group = radiosForOption(pi, optName, sectionId);
      group.forEach(function (radio) {
        var match = String(radio.value) === String(expected);
        if (radio.checked !== match) radio.checked = match;
        var label = findLabelFor(pi, radio);
        if (label) {
          if (match) label.setAttribute('aria-current', 'true');
          else label.removeAttribute('aria-current');
        }
      });
      // Select dropdown
      var sel = selectForOption(pi, optName, sectionId);
      if (sel && sel.value !== expected) sel.value = expected;
    });
  }

  /* Options whose visibility filter is SKIPPED — they always show
     every value regardless of compatibility with other selections.
     Case Colour is the "primary" decision point for the customer;
     we want all case colours selectable, and dependent options
     (strap, buckle, etc.) adapt around the chosen case. Match is
     case-insensitive, whitespace-stripped, US + UK spellings. */
  function isAlwaysVisibleOption(optName) {
    var lc = String(optName || '').toLowerCase().replace(/\s+/g, '');
    return lc === 'casecolour' || lc === 'casecolor';
  }

  /* Grey out option values that aren't purchasable with the current
     selections of the OTHER options — sold out, or no such variant at
     all. NOTHING IS EVER HIDDEN: every value stays on the page and
     stays clickable, and clicking a greyed one lets the resolver move
     the other options to a combination that exists.

     Runs after every resolver pass, and mirrors the server-side rule
     in snippets/product-variant-options.liquid so the two agree
     between a click and the section re-render it triggers. */
  function updateOptionAvailability(pi) {
    var variants = readVariants(pi);
    if (!Array.isArray(variants) || !variants.length) return;

    var optionNames = getOptionNamesInOrder(pi);
    var sectionId = getSectionId(pi);
    var currentSelections = getCurrentSelections(pi, optionNames);

    optionNames.forEach(function (optName, idx) {
      var radios = radiosForOption(pi, optName, sectionId);

      // Always-visible options: clear any hidden state and skip
      if (isAlwaysVisibleOption(optName)) {
        radios.forEach(function (radio) {
          radio.disabled = false;
          radio.removeAttribute('data-mgb-incompatible');
          radio.classList.remove('visually-disabled');
          // Also clear any inline display:none from previous code paths
          if (radio.style && radio.style.display === 'none') radio.style.display = '';
          var label = findLabelFor(pi, radio);
          if (label && label.style && label.style.display === 'none') label.style.display = '';
        });
        return;
      }

      radios.forEach(function (radio) {
        // Build hypothetical selection: this radio's value at its
        // option slot, current selections at all other slots.
        var hypothetical = currentSelections.slice();
        hypothetical[idx] = radio.value;

        // Find the variant for that combination. Keep looking after a
        // hit until an in-stock one turns up, so a value is only
        // greyed when EVERY matching variant is unavailable — with a
        // partial selection (an option still unset) several variants
        // can match and one in stock is enough to offer it.
        var matched = null;
        for (var vi = 0; vi < variants.length; vi++) {
          var v = variants[vi];
          if (!v.options) continue;
          var match = true;
          for (var oi = 0; oi < hypothetical.length; oi++) {
            if (hypothetical[oi] != null && v.options[oi] !== hypothetical[oi]) {
              match = false; break;
            }
          }
          if (match) {
            matched = v;
            if (v.available) break;
          }
        }

        var purchasable = !!(matched && matched.available);
        var label = findLabelFor(pi, radio);

        // Never hide. Clear any display:none left in the DOM by an
        // earlier build of this script before the greying replaced it.
        [radio, label].filter(Boolean).forEach(function (el) {
          if (el.style && el.style.display === 'none') el.style.display = '';
        });

        // Stays enabled on purpose. `visually-disabled` greys it
        // without setting pointer-events, so the value is still
        // clickable and the resolver can move the other options to a
        // combination that exists. A real `disabled` would make a
        // greyed colour a dead end.
        radio.disabled = false;
        if (purchasable) {
          radio.classList.remove('visually-disabled');
          radio.removeAttribute('data-mgb-incompatible');
        } else {
          radio.classList.add('visually-disabled');
          radio.setAttribute('data-mgb-incompatible', '');
        }
      });
    });
    log('updateOptionAvailability done; selections =', currentSelections);
  }

  function needsCorrection(selections, variant) {
    if (!variant || !variant.options) return false;
    for (var i = 0; i < selections.length; i++) {
      if (selections[i] != null && selections[i] !== variant.options[i]) return true;
    }
    return false;
  }

  function runResolver(pi, pinnedOpt) {
    // pinnedOpt: optional { optionIdx, value } to preserve
    var variants = readVariants(pi);
    if (!Array.isArray(variants) || !variants.length) {
      log('runResolver: no variants array');
      return false;
    }
    var optionNames = getOptionNamesInOrder(pi);
    var selections = getCurrentSelections(pi, optionNames);
    var pinnedIdx = pinnedOpt ? pinnedOpt.optionIdx : -1;

    // If a pinned option is provided, override its slot in selections
    // so the resolver respects user intent even after Shopify's
    // re-render reset things.
    if (pinnedOpt && pinnedIdx >= 0) {
      selections[pinnedIdx] = pinnedOpt.value;
    }

    log('runResolver INPUT', {
      optionNames: optionNames,
      selections: selections,
      pinnedIdx: pinnedIdx,
      pinnedOpt: pinnedOpt,
      variantCount: variants.length
    });

    var best = findBestVariant(variants, selections, pinnedIdx);
    if (!best || !best.options) {
      log('runResolver: no variant found for', selections);
      return false;
    }
    if (!needsCorrection(selections, best)) {
      log('no correction needed — current selection IS valid:', selections, 'matches', best.options);
      // Still refresh availability filter so visibility stays in sync
      // when the user changes an option to a value that's still valid
      // but should now hide incompatible peers.
      updateOptionAvailability(pi);
      return false;
    }

    log('CORRECTING', selections, '→', best.options, '(variant id ' + best.id + ', available=' + best.available + ')');
    SYNC_FLAG = true;
    applyVariant(pi, best, optionNames);
    SYNC_FLAG = false;
    updateOptionAvailability(pi);
    return true;
  }

  // ── LAYER 1: capture-phase intercept ──────────────────────────
  function onCaptureChange(e) {
    log('LAYER 1: change event fired', {
      targetTag: e.target && e.target.tagName,
      targetType: e.target && e.target.type,
      targetName: e.target && e.target.name,
      targetValue: e.target && e.target.value,
      syncFlag: SYNC_FLAG
    });
    if (SYNC_FLAG) return;
    var t = e.target;
    if (!t) return;
    var isRadio = t.type === 'radio';
    var isSelect = t.tagName === 'SELECT';
    if (!isRadio && !isSelect) { log('LAYER 1: not radio/select, skip'); return; }

    var pi = t.closest('product-info');
    if (!pi) { log('LAYER 1: no product-info ancestor'); return; }

    // Must be inside a variant-selects element
    if (!t.closest('variant-selects')) { log('LAYER 1: not inside variant-selects, skip'); return; }

    var sectionId = getSectionId(pi);
    var optName = parseOptionName(t, sectionId);
    if (!optName) {
      var codes = [];
      try { codes = Array.from(String(t.name || '')).map(function (c) { return c.charCodeAt(0); }); } catch (e) {}
      log('LAYER 1: could not parse option name', {
        rawName: t.name,
        rawNameJSON: JSON.stringify(t.name),
        charCodes: codes,
        sectionId: sectionId,
        hasFieldset: !!(t.closest && t.closest('fieldset')),
        hasLegend: !!(t.closest && t.closest('fieldset') && t.closest('fieldset').querySelector('legend'))
      });
      return;
    }

    var optionNames = getOptionNamesInOrder(pi);
    var pinnedIdx = optionNames.indexOf(optName);

    // Record user intent for later layers
    lastUserIntent = {
      optionName: optName,
      value: t.value,
      optionIdx: pinnedIdx,
      pi: pi,
      at: Date.now()
    };
    log('LAYER 1: user intent recorded', lastUserIntent);

    runResolver(pi, { optionIdx: pinnedIdx, value: t.value });
  }
  document.addEventListener('change', onCaptureChange, { capture: true });
  log('resolver installed; variant count =', (function () {
    var pi = document.querySelector('product-info');
    if (!pi) return 'no product-info';
    var v = readVariants(pi);
    return v ? v.length : 'no variants JSON';
  })());

  // ── LAYER 2: variant:change listener ──────────────────────────
  // After Shopify resolves, verify intent was preserved. If not,
  // re-correct and re-trigger.
  document.addEventListener('variant:change', function (e) {
    if (SYNC_FLAG) return;
    if (!lastUserIntent) return;
    if (Date.now() - lastUserIntent.at > 5000) return;

    var pi = (e.target && e.target.closest) ? e.target.closest('product-info') : null;
    pi = pi || lastUserIntent.pi || document.querySelector('product-info');
    if (!pi || !pi.isConnected) {
      // Section was re-rendered — find the new product-info
      pi = document.querySelector('product-info');
      if (!pi) return;
    }

    var resolved = e.detail && e.detail.variant;
    var intentHonoured = resolved
      && resolved.options
      && resolved.options[lastUserIntent.optionIdx] === lastUserIntent.value;

    if (intentHonoured) {
      log('intent honoured by shopify');
      return;
    }

    log('intent NOT honoured — re-correcting');
    var corrected = runResolver(pi, {
      optionIdx: lastUserIntent.optionIdx,
      value: lastUserIntent.value
    });

    if (corrected) {
      // Re-trigger Shopify's resolution with corrected state
      setTimeout(function () {
        var anyChecked = pi.querySelector('input[type="radio"][name^="options["]:checked');
        if (anyChecked) {
          SYNC_FLAG = true;
          try { anyChecked.dispatchEvent(new Event('change', { bubbles: true })); }
          catch (err) {}
          SYNC_FLAG = false;
        }
      }, 0);
    }
  });

  // ── LAYER 3: MutationObserver on section re-render ────────────
  // Shopify replaces product-info subtree HTML after variant change.
  // Two things to do whenever the section mutates:
  //   • Always refresh availability filter (so hidden options stay
  //     hidden after re-render — the data attribute is wiped, and the
  //     CSS rule only acts when the attribute is set).
  //   • Re-apply user intent IF we're still inside the 5s window
  //     after a user click (defensive against Shopify reverting our
  //     correction during re-render).
  function observePI(pi) {
    if (!pi || pi.__mgbObserved) return;
    pi.__mgbObserved = true;
    var visDebounce = null;
    var intentDebounce = null;
    new MutationObserver(function (mutations) {
      if (SYNC_FLAG) return;
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          relevant = true; break;
        }
      }
      if (!relevant) return;

      // Always refresh availability — covers re-render wiping our
      // data-mgb-incompatible attributes.
      //
      // Run it SYNCHRONOUSLY first. A MutationObserver callback is a
      // microtask, so it runs after the nodes are inserted but before
      // the browser paints them — filtering here means an unavailable
      // value is never shown at all. The 50ms debounce below used to
      // be the only pass, which is why incompatible strap colours
      // appeared for a moment after every case-colour change.
      var piNow = document.querySelector('product-info') || pi;
      updateOptionAvailability(piNow);

      // Debounced pass as well, to batch any follow-up mutations
      // (the resolver can correct the selection just after a
      // re-render, which changes what is compatible).
      clearTimeout(visDebounce);
      visDebounce = setTimeout(function () {
        var currentPi = document.querySelector('product-info') || pi;
        log('section mutated — refreshing availability filter');
        updateOptionAvailability(currentPi);
      }, 50);

      // Re-apply user intent if recent
      if (!lastUserIntent || Date.now() - lastUserIntent.at > 5000) return;
      clearTimeout(intentDebounce);
      intentDebounce = setTimeout(function () {
        log('section re-rendered — re-applying user intent');
        var currentPi = document.querySelector('product-info') || pi;
        runResolver(currentPi, {
          optionIdx: lastUserIntent.optionIdx,
          value: lastUserIntent.value
        });
      }, 50);
    }).observe(pi, { childList: true, subtree: true });
  }

  // ── Initial pass on load ──────────────────────────────────────
  function initialPass() {
    document.querySelectorAll('product-info').forEach(function (pi) {
      runResolver(pi, null);
      // runResolver only calls updateOptionAvailability when it
      // actually evaluated a variant; if the page loaded with an
      // already-valid combo AND no resolver call happened, force a
      // visibility pass here so initial render is correct.
      updateOptionAvailability(pi);
      observePI(pi);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialPass);
  } else {
    initialPass();
  }
})();
